import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cn } from '../../../utils/cn';
import { Icon } from '../Icon/Icon';
import { Spinner } from '../Spinner/Spinner';

export type IconButtonVariant = 'ghost' | 'subtle' | 'outline' | 'primary' | 'secondary' | 'danger' | 'onDark';
type Size = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: ReactNode;
  label: string;
  variant?: IconButtonVariant;
  size?: Size;
  loading?: boolean;
}

const VARIANTS: Record<IconButtonVariant, string> = {
  ghost: 'text-navy-600 hover:bg-navy-100/70 active:bg-navy-100',
  subtle: 'bg-navy-50 text-navy-800 hover:bg-navy-100',
  outline: 'border border-line bg-white text-navy-800 hover:border-navy-300 hover:bg-navy-50',
  primary: 'bg-brand-600 text-white hover:bg-brand-700',
  secondary: 'bg-navy-800 text-white hover:bg-navy-700',
  danger: 'text-red-600 hover:bg-red-50',
  onDark: 'text-white/80 hover:bg-white/10 hover:text-white',
};

const SIZES: Record<Size, string> = { sm: 'h-9 w-9 rounded-lg', md: 'h-10 w-10 rounded-lg', lg: 'h-12 w-12 rounded-lg' };
const ICON: Record<Size, number> = { sm: 18, md: 20, lg: 22 };

/** Square icon-only button. `label` is required: it is read by screen readers and shown as a tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant = 'ghost', size = 'md', loading, className, type = 'button', disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      className={cn(
        'inline-flex shrink-0 items-center justify-center transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-steel focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-40',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={ICON[size]} /> : typeof icon === 'string' ? <Icon name={icon} size={ICON[size]} /> : icon}
    </button>
  );
});

export default IconButton;
